import type { RevvlyStatusState } from '@admin-platform/ui/components';

import type { PlatformStatusSnapshot } from '../../hooks/usePlatformStatus';

/** `v4.18.0` reads as `v4.18`: a minor is named by its minor. */
export const shortVersion = (version: string): string => version.replace(/\.0$/, '');

/**
 * Which face the Revvly icon shows. Something wrong with Revvly outranks any news about it:
 * unreachable or down, then degraded, then an unread minor or major release, then a patch.
 */
export function iconStateFor(
  platform: PlatformStatusSnapshot['state'],
  unread: { count: number; kind?: string | null },
): RevvlyStatusState {
  if (platform === 'unreachable' || platform === 'down') return 'down';
  if (platform === 'degraded') return 'degraded';
  if (unread.count > 0) return unread.kind === 'PATCH' ? 'small' : 'major';
  return 'idle';
}

/** The hover label and accessible name for each face. Plain words, no internals. */
export function iconLabel(
  state: RevvlyStatusState,
  platform: PlatformStatusSnapshot,
  version?: string | null,
): string {
  // A posted incident says it best: its title comes before any component name.
  if ((state === 'down' || state === 'degraded') && platform.incident) {
    return platform.incident.title;
  }
  switch (state) {
    case 'down':
      return platform.state === 'unreachable'
        ? "Can't reach Revvly. Retrying"
        : 'Revvly is having a problem';
    case 'degraded': {
      const slow = platform.components.filter((c) => c.state !== 'ok').map((c) => c.name);
      if (slow.length === 0) return 'Part of Revvly is slow';
      return `${slow.join(' and ')} ${slow.length === 1 ? 'is' : 'are'} slow`;
    }
    case 'major':
      return version ? `New feature · ${shortVersion(version)}` : 'New feature';
    case 'small':
      return version ? `New update · ${version}` : 'New update';
    default:
      return 'Release log';
  }
}
